import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiMic, FiSend, FiTrash2, FiZap } from 'react-icons/fi';
import BottomNav from '../shared/BottomNav.jsx';
import Navbar from '../shared/Navbar.jsx';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import '../home/Home.css';
import './AssistantIaPage.css';

const API_BASE = getApiBaseUrl();

const CHAT_STORAGE_KEY = 'uxd-ai-chat-history';
const MAX_STORED_MESSAGES = 200;

function loadMessages() {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveMessages(msgs) {
  try {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(msgs.slice(-MAX_STORED_MESSAGES)));
  } catch {}
}

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const QUICK_CHIPS = [
  { label: 'Voyage en famille', icon: '👨‍👩‍👧' },
  { label: 'Business trip', icon: '💼' },
  { label: 'Budget serré', icon: '💰' },
  { label: 'Trajet Marrakech', icon: '🗺️' },
  { label: 'Voiture électrique', icon: '⚡' },
  { label: 'Véhicule de luxe', icon: '✨' },
];


/* ── Web Speech API ─────────────────────────────────────────────── */
const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

function useSpeechRecognition(onResult, onError) {
  const recogRef = useRef(null);

  const start = useCallback(() => {
    if (!SpeechRecognition) {
      onError('Ce navigateur ne supporte pas la reconnaissance vocale.');
      return;
    }
    const recog = new SpeechRecognition();
    recog.lang = 'fr-FR';
    recog.interimResults = false;
    recog.maxAlternatives = 1;
    recog.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      if (transcript?.trim()) onResult(transcript.trim());
    };
    recog.onerror = (e) => {
      if (e.error !== 'no-speech') onError('Micro inaccessible ou refusé.');
    };
    recog.onend = () => { recogRef.current = null; };
    recogRef.current = recog;
    recog.start();
  }, [onResult, onError]);

  const stop = useCallback(() => {
    recogRef.current?.stop();
    recogRef.current = null;
  }, []);

  return { start, stop, supported: !!SpeechRecognition };
}

/* ── Message bubble (memoized for performance) ──────────────────── */
const MessageBubble = ({ msg }) => {
  const isUser = msg.role === 'user';

  const renderText = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) =>
      line.trim() === ''
        ? <br key={i} />
        : <span key={i} className="ai-text-line">{line}</span>
    );
  };

  return (
    <>
      <div className={`ai-bubble${isUser ? ' is-user' : ' is-ai'}`}>
        {!isUser && (
          <div className="ai-bubble-avatar" aria-hidden="true">
            <img src="/assets/images/logo.png" alt="" />
          </div>
        )}
        <div className="ai-bubble-body">
          <div className="ai-bubble-meta">
            <span className="ai-bubble-role">{isUser ? 'Vous' : 'Assistant'}</span>
            {msg.ts && <span className="ai-bubble-time">{formatTime(msg.ts)}</span>}
          </div>
          <div className="ai-bubble-text">
            {renderText(msg.text)}
          </div>
        </div>
      </div>
      {/* Recommandations inline après chaque réponse IA (visible surtout sur mobile) */}
      {!isUser && msg.suggestions?.length > 0 && (
        <div className="ai-inline-suggestions">
          {msg.suggestions.map((s, i) => (
            <SuggestionCard key={s.id || i} s={s} index={i} />
          ))}
        </div>
      )}
    </>
  );
};

/* ── Suggestion card (memoized) ──────────────────────────────────── */
const SuggestionCard = ({ s, index }) => (
  <Link
    to={s.id ? `/vehicle/${s.id}` : '/search'}
    className="ai-scard"
    aria-label={`Voir ${s.label}`}
  >
    <div className="ai-scard-img-wrap">
      {s.photo ? (
        <img src={s.photo} alt={s.label} className="ai-scard-img" loading="lazy" />
      ) : (
        <div className="ai-scard-img-placeholder">
          <span>🚗</span>
        </div>
      )}
      {s.badge && <span className="ai-scard-badge">{s.badge}</span>}
    </div>
    <div className="ai-scard-info">
      <span className="ai-scard-num">0{index + 1}</span>
      <strong className="ai-scard-model">{s.label}</strong>
      <span className="ai-scard-type">
        {s.type}{s.city ? ` · ${s.city}` : ''}
        {s.transmission ? ` · ${s.transmission}` : ''}
      </span>
      <div className="ai-scard-footer">
        <strong className="ai-scard-price">
          {s.pricePerDay ? `${s.pricePerDay} MAD/j` : '—'}
        </strong>
        <span className="ai-scard-cta">
          Voir <FiArrowRight size={11} />
        </span>
      </div>
    </div>
  </Link>
);

/* ═══════════════════════════════════════════════════════════════════ */

function AssistantIaPage() {
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState(() => loadMessages());
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);

  /* ── persist ── */
  useEffect(() => { saveMessages(messages); }, [messages]);

  /* ── scroll to bottom ── */
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const el = chatBodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  useEffect(() => {
    scrollToBottom(messages.length <= 1 ? 'instant' : 'smooth');
  }, [messages, isLoading, scrollToBottom]);

  /* ── scroll indicator ── */
  const handleScroll = useCallback(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distFromBottom > 120);
  }, []);

  /* ── voice ── */
  const onVoiceResult = useCallback((transcript) => {
    setIsRecording(false);
    sendMessage(transcript);
  }, []); // eslint-disable-line

  const onVoiceError = useCallback((errMsg) => {
    setIsRecording(false);
    setMessages((prev) => [...prev, { role: 'ai', text: errMsg, ts: Date.now() }]);
  }, []);

  const { start: startRecog, stop: stopRecog, supported: voiceSupported } =
    useSpeechRecognition(onVoiceResult, onVoiceError);

  const handleMicClick = useCallback(() => {
    if (isRecording) { stopRecog(); setIsRecording(false); }
    else { setIsRecording(true); startRecog(); }
  }, [isRecording, startRecog, stopRecog]);

  /* ── API call ── */
  const sendMessage = useCallback(async (text) => {
    const clean = text?.trim();
    if (!clean || isLoading) return;

    const history = messages.slice(-10).map((m) => ({
      role: m.role === 'user' ? 'user' : 'assistant',
      content: m.text,
    }));

    setMessages((prev) => [...prev, { role: 'user', text: clean, ts: Date.now() }]);
    setPrompt('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: clean, history }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Erreur ${res.status}`);
      }

      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        {
          role: 'ai',
          text: data.message || "Je n'ai pas pu répondre.",
          suggestions: data.suggestions || [],
          ts: Date.now(),
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'ai', text: `Désolé, une erreur est survenue : ${err.message}`, ts: Date.now() },
      ]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isLoading, messages]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(prompt); }
  }, [prompt, sendMessage]);

  const clearHistory = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(CHAT_STORAGE_KEY);
  }, []);

  const latestSuggestions = useMemo(() =>
    [...messages].reverse().find((m) => m.role === 'ai' && m.suggestions?.length > 0)?.suggestions || [],
    [messages]
  );

  /* ═══════════════════════ RENDER ═══════════════════════════════ */
  return (
    <div className="ai-page">
      <div className="vx-noise" aria-hidden="true" />

      <Navbar />

      {/* ── MAIN ── */}
      <main className="ai-main">

        {/* Hero */}
        <section className="ai-hero">
          <div className="vx-shell">
            <div className="ai-hero-inner">
              <div className="ai-hero-eyebrow">
                <span className="ai-live-dot" aria-hidden="true" />
                <span>Intelligence artificielle</span>
                <span className="ai-hero-sep" aria-hidden="true">·</span>
                <span>Recommandation personnalisée</span>
              </div>
              <h1 className="ai-hero-title">
                Votre assistant <em>automobile</em>
              </h1>
              <p className="ai-hero-sub">
                Décrivez votre besoin — destination, budget, occasions — et l'IA vous recommande le véhicule idéal parmi notre flotte certifiée.
              </p>
            </div>
          </div>
        </section>

        {/* Chat + Sidebar */}
        <section className="ai-body">
          <div className="vx-shell">
            <div className="ai-layout">

              {/* ── CHAT CARD ── */}
              <div className="ai-chat-card">

                {/* Header */}
                <div className="ai-chat-header">
                  <div className="ai-chat-header-left">
                    <div className="ai-agent-avatar">
                      <img src="/assets/images/logo.png" alt="Assistant IA" />
                    </div>
                    <div className="ai-agent-info">
                      <strong className="ai-agent-name">Assistant UltimateXDrive</strong>
                      <span className={`ai-agent-status${isLoading ? ' is-thinking' : isRecording ? ' is-recording' : ''}`}>
                        <span className="ai-status-dot" aria-hidden="true" />
                        {isLoading
                          ? 'Réflexion en cours…'
                          : isRecording
                            ? 'Écoute en cours…'
                            : 'En ligne · Prêt'}
                      </span>
                    </div>
                  </div>
                  <div className="ai-chat-header-actions">
                    <Link to="/search" className="ai-header-link">
                      <FiZap size={13} />
                      Recherche complète
                    </Link>
                    {messages.length > 0 && (
                      <button
                        type="button"
                        className="ai-clear-btn"
                        onClick={clearHistory}
                        title="Effacer la conversation"
                        aria-label="Effacer la conversation"
                      >
                        <FiTrash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Quick chips */}
                <div className="ai-chips-row" role="group" aria-label="Suggestions rapides">
                  {QUICK_CHIPS.map(({ label, icon }) => (
                    <button
                      key={label}
                      type="button"
                      className="ai-chip"
                      onClick={() => sendMessage(label)}
                      disabled={isLoading || isRecording}
                    >
                      <span className="ai-chip-icon" aria-hidden="true">{icon}</span>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Messages */}
                <div
                  className="ai-chat-body"
                  ref={chatBodyRef}
                  onScroll={handleScroll}
                  role="log"
                  aria-label="Conversation"
                  aria-live="polite"
                >
                  {messages.length === 0 && !isLoading && (
                    <div className="ai-empty">
                      <div className="ai-empty-icon" aria-hidden="true">
                        <img src="/assets/images/logo.png" alt="" />
                      </div>
                      <p className="ai-empty-title">Comment puis-je vous aider ?</p>
                      <p className="ai-empty-sub">
                        Décrivez votre besoin ou choisissez un raccourci ci-dessus.
                      </p>
                      <div className="ai-empty-hints" aria-label="Exemples de messages">
                        <span>"Je cherche un SUV automatique à Casablanca"</span>
                        <span>"Voiture électrique budget 400 DH/j"</span>
                        <span>"Besoin d'un monospace 7 places"</span>
                      </div>
                    </div>
                  )}

                  {messages.map((msg, i) => (
                    <MessageBubble key={i} msg={msg} />
                  ))}

                  {isLoading && (
                    <div className="ai-bubble is-ai is-loading">
                      <div className="ai-bubble-avatar" aria-hidden="true">
                        <img src="/assets/images/logo.png" alt="" />
                      </div>
                      <div className="ai-bubble-body">
                        <div className="ai-bubble-meta">
                          <span className="ai-bubble-role">Assistant</span>
                        </div>
                        <div className="ai-bubble-text">
                          <div className="ai-typing" aria-label="L'assistant réfléchit…">
                            <span /><span /><span />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Scroll to bottom button */}
                {showScrollBtn && (
                  <button
                    type="button"
                    className="ai-scroll-btn"
                    onClick={() => scrollToBottom('smooth')}
                    aria-label="Défiler vers le bas"
                  >
                    ↓
                  </button>
                )}

                {/* Input */}
                <div className="ai-input-bar">
                  <button
                    type="button"
                    className={`ai-mic-btn${isRecording ? ' is-recording' : ''}`}
                    onClick={handleMicClick}
                    disabled={isLoading || !voiceSupported}
                    aria-label={isRecording ? "Arrêter l'écoute" : 'Parler'}
                    title={!voiceSupported ? 'Reconnaissance vocale non supportée' : undefined}
                  >
                    <FiMic size={15} />
                  </button>

                  <div className="ai-input-wrap">
                    <input
                      ref={inputRef}
                      type="text"
                      className="ai-input"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={
                        isRecording
                          ? 'Parlez maintenant…'
                          : 'Décrivez votre besoin ou tapez un message…'
                      }
                      disabled={isLoading || isRecording}
                      aria-label="Message à l'assistant"
                      autoComplete="off"
                    />
                  </div>

                  <button
                    type="button"
                    className="ai-send-btn"
                    onClick={() => sendMessage(prompt)}
                    disabled={!prompt.trim() || isLoading || isRecording}
                    aria-label="Envoyer"
                  >
                    <FiSend size={14} />
                  </button>
                </div>

                <div className="ai-input-hint">
                  Appuyez sur <kbd>Entrée</kbd> pour envoyer · <kbd>Shift+Entrée</kbd> pour nouvelle ligne
                </div>
              </div>

              {/* ── SUGGESTIONS SIDEBAR ── */}
              <aside className="ai-sidebar" aria-label="Véhicules recommandés">
                <div className="ai-sidebar-header">
                  <span className="ai-sidebar-kicker">Recommandations IA</span>
                  <h2 className="ai-sidebar-title">Véhicules suggérés</h2>
                </div>

                {latestSuggestions.length > 0 ? (
                  <div className="ai-scard-list">
                    {latestSuggestions.map((s, i) => (
                      <SuggestionCard key={s.id || i} s={s} index={i} />
                    ))}
                  </div>
                ) : (
                  <div className="ai-sidebar-empty">
                    <div className="ai-sidebar-empty-icon" aria-hidden="true">
                      <img src="/assets/images/logo.png" alt="" />
                    </div>
                    <p>Vos recommandations apparaîtront ici après votre premier message.</p>
                    <div className="ai-sidebar-hints">
                      <span>Essayez : "Voyage en famille"</span>
                      <span>Ou : "Budget 500 DH"</span>
                      <span>Ou : "Trajet Marrakech"</span>
                    </div>
                  </div>
                )}

                <Link to="/search" className="ai-sidebar-cta">
                  Explorer toute la flotte <FiArrowRight size={13} />
                </Link>
              </aside>

            </div>
          </div>
        </section>
      </main>

      <BottomNav />
    </div>
  );
}

export default AssistantIaPage;
